import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import classes from './EditFlowName.module.scss';
import { Stack } from 'new/elements/Stack';
import { TextField } from 'new/elements/TextField';
import Typography from 'new/elements/TypographySupport';
import useEntities from 'new/state/entities/repository/useEntities';
import { useDebouncedCallback } from 'new/hooks/useDebouncedCallback';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { canUpdateResource } from 'js/lib/flowValidationUtils';
import { getResourceEntity, isCapsuleCluster } from 'js/lib/PipelineUtils';
import { CapsuleClusterChip } from 'new/modules/settings/capsules/components/CapsuleClusterChip/CapsuleClusterChip';
import { ProjectPipelineChip } from 'js/components/projects/chips';
import PipelineStatusChip from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { useFlatFlowsById } from 'new/hooks/adapters/useFlatFlows';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { ConnectionTypeChip } from 'new/elements/ConnectionTypeChip/ConnectionTypeChip';
import FlowTypeIcon from 'js/components/pipelines/cards/FlowTypeIcon';
import DataFlowDetailActionsContainer from 'js/components/data_flows/DataFlowDetailActionsContainer';
import ModalCloneFlowContainer from 'js/components/pipelines/ModalCloneFlowContainer';
import { useFlowDuplicate } from 'new/hooks/useFlowDuplicate';
import { useAppContext } from 'new/hooks/useAppContext';

const DATE_FORMAT = 'MMM dd, yyyy';

interface EditFlowNameProps {
  flowId: number;
  embedded?: boolean;
}

export const EditFlowName = (props: EditFlowNameProps) => {
  const { flowId, embedded } = props;
  const { mode } = useAppContext();
  const isDark = mode === 'dark';
  const { data: flow } = useEntities(`flowNodes[${flowId}]`);
  const { data: allEntities } = useEntities();
  const { updatePipeline } = usePipelinesAdapter();
  const flatFlowsById = useFlatFlowsById();
  const flatFlow = flatFlowsById[Number(flowId)];
  const rootConnectionIcon = flatFlow?.rootConnectionIcon;
  const rootConnectionName = flatFlow?.rootConnectionName;
  const { cloneFlowData, toggleCloneFlowModal, handleDuplicateSuccess } = useFlowDuplicate(flowId);

  const mergedData = flow ? getResourceEntity(flow, allEntities) : null;
  const project = flow?.project_id ? allEntities?.projects?.[flow.project_id] : null;

  const updateValidation = canUpdateResource(flow);
  const isDisabled = !updateValidation.canActivate;

  const [name, setName] = useState(flow?.name || '');
  const [description, setDescription] = useState(flow?.description || '');

  useEffect(() => {
    if (flow) {
      setName((prev) => {
        const next = flow.name || '';
        return next !== prev ? next : prev;
      });
      setDescription((prev) => {
        const next = flow.description || '';
        return next !== prev ? next : prev;
      });
    }
  }, [flow?.name, flow?.description]);

  const debouncedUpdateName = useDebouncedCallback(
    (value: string) => {
      if (!flow?.id || typeof flow.id !== 'number') return;
      updatePipeline(flow.id, { name: value });
    },
    [flow],
    700,
  );

  const debouncedUpdateDescription = useDebouncedCallback(
    (value: string) => {
      if (!flow?.id || typeof flow.id !== 'number') return;
      updatePipeline(flow.id, { description: value });
    },
    [flow],
    700,
  );

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    debouncedUpdateName(event.target.value);
  };

  const handleDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
    debouncedUpdateDescription(event.target.value);
  };

  if (!flow) return null;

  const sharedInputSx = {
    padding: '1px 8px',
    fontSize: '14px',
    lineHeight: '20px',
    borderColor: 'transparent',
    backgroundColor: 'transparent',
    cursor: isDisabled ? 'default' : 'text',
    '&:hover:not(:focus-within)': {
      borderColor: isDisabled
        ? 'transparent'
        : isDark
          ? 'rgba(255, 255, 255, 0.2)'
          : 'var(--color-greyscale-200)',
    },
    '&.Mui-disabled': { backgroundColor: 'transparent' },
  };

  const nameInputSx = {
    ...sharedInputSx,
    fontWeight: 500,
    color: isDark ? '#ffffff' : 'var(--color-greyscale-1000)',
  };

  const descriptionInputSx = {
    ...sharedInputSx,
    fontWeight: 300,
    color: isDark ? 'rgba(255, 255, 255, 0.55)' : 'var(--color-greyscale-500)',
    '&:focus-within': { color: isDark ? '#ffffff' : 'var(--color-greyscale-1000)' },
  };

  const showConnectionChip =
    flow?.flow_type !== 'rag' &&
    flow?.flow_type !== 'api_server' &&
    mergedData?.flow_type !== 'rag' &&
    mergedData?.flow_type !== 'api_server';

  return (
    <>
      <div className={`${classes.container}${isDark ? ` ${classes.dark}` : ''}`}>
        <div className={classes.left}>
          <Stack gap="2px">
            <div className={classes.fieldWrapper}>
              <TextField
                value={name}
                name="name"
                onChange={handleNameChange}
                disabled={isDisabled}
                fullWidth
                InputProps={{ sx: nameInputSx }}
              />
            </div>
            <div className={classes.fieldWrapper}>
              <TextField
                value={description}
                name="description"
                onChange={handleDescriptionChange}
                disabled={isDisabled}
                fullWidth
                inputProps={{ placeholder: 'Add a description…' }}
                InputProps={{ sx: descriptionInputSx }}
              />
            </div>
            <Stack direction="row" gap="16px" alignItems="center" paddingLeft="var(--spacing-md)">
              {showConnectionChip && (rootConnectionIcon || rootConnectionName) && (
                <ConnectionTypeChip
                  icon={rootConnectionIcon}
                  label={rootConnectionName}
                  style={{ border: 'none', padding: 0 }}
                />
              )}
              <ChipWithCopy toCopy={String(flowId)}>ID {flowId}</ChipWithCopy>
              {mergedData && (
                <FlowTypeIcon data={mergedData} className={classes.flowTypeContainer} />
              )}
              {isCapsuleCluster(mergedData) && (
                <CapsuleClusterChip name={mergedData.cluster.name} />
              )}
            </Stack>
          </Stack>
        </div>

        <div className={classes.right}>
          <ProjectPipelineChip id={project?.id} name={project?.name} disabled={embedded} />
          <div className={classes.innerRight}>
            <div className={classes.rightActions}>
              {flow?.status && (
                <PipelineStatusChip
                  id={flow.id}
                  status={flow.status}
                  archivalStatus={flow.archival_status}
                  showRuntimeIndicator={false}
                />
              )}
              {mergedData && (
                <DataFlowDetailActionsContainer
                  data={mergedData}
                  inPipeline
                  isExpandedCanvas
                  embedded={embedded}
                  toggleCloneFlowModal={toggleCloneFlowModal}
                />
              )}
            </div>
            <div className={classes.rightCreatedAt}>
              <Typography
                variant="body3"
                sx={isDark ? { color: 'rgba(255, 255, 255, 0.7) !important' } : undefined}
              >
                {mergedData?.created_at
                  ? `Created on ${format(new Date(mergedData.created_at), DATE_FORMAT)}`
                  : ''}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      {cloneFlowData && (
        <ModalCloneFlowContainer
          data={cloneFlowData}
          toggleCloneFlowModal={toggleCloneFlowModal}
          setSelectedPipeline={handleDuplicateSuccess}
        />
      )}
    </>
  );
};
